import type { ReactElement } from 'react'
import { useState } from 'react'
import { AddressInput } from '~/components/Form/Input/AddressInput'
import { Dropdown } from '~/components/Form/Input/Dropdown'
import { formatAddress } from '~/domains/common/utils'
import {
  encodeProximity,
  type Proximity,
  parseProximity
} from '~/domains/companies/proximity'
import { proximityEditor } from './ProximityFilterEditor.css'

const DEFAULT_RADIUS = 20

const RADIUS_OPTIONS = [5, 10, 20, 50, 100].map((radius) => ({
  label: `${radius} km`,
  value: String(radius)
}))

type ProximityFilterEditorProps = {
  value: string
  onChange: (value: string) => void
  onClose: () => void
}

export const ProximityFilterEditor = ({
  value,
  onChange,
  onClose
}: ProximityFilterEditorProps): ReactElement => {
  const initial = parseProximity(value)
  const [location, setLocation] = useState<Omit<Proximity, 'radius'> | null>(
    initial
  )
  const [radius, setRadius] = useState(initial?.radius ?? DEFAULT_RADIUS)

  const apply = (
    next: Omit<Proximity, 'radius'> | null,
    nextRadius: number
  ) => {
    if (!next) return
    onChange(encodeProximity({ ...next, radius: nextRadius }))
  }

  return (
    <div className={proximityEditor}>
      <AddressInput
        placeholder="Ville ou adresse"
        onChange={(address) => {
          if (address.lat == null || address.lng == null) return
          const next = {
            lat: address.lat,
            lng: address.lng,
            label: formatAddress(address, { showLines: false })
          }
          setLocation(next)
          apply(next, radius)
          onClose()
        }}
      />
      <Dropdown
        options={RADIUS_OPTIONS}
        defaultValue={String(radius)}
        small
        fullWidth
        onChange={(nextValue) => {
          const nextRadius = Number(nextValue) || DEFAULT_RADIUS
          setRadius(nextRadius)
          apply(location, nextRadius)
        }}
      />
    </div>
  )
}
